import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  NgZone,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ModalActionButton,
  ModalConfig,
  ModalResult,
  ModalService,
} from '../../../core/services/modal.service';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';

/**
 * Dynamic modal dialog wrapper component.
 * Renders the modal chrome (header, content area, footer with action buttons).
 *
 * This is SEPARATE from the existing simple `app-modal-dialog` component.
 * This component is used by the dynamic modal system (ModalService + ModalContainerComponent).
 *
 * Responsibilities:
 * - Render modal wrapper structure (header, content, footer)
 * - Handle header close button click
 * - Render action buttons with click handlers
 * - Handle escape key and backdrop click
 * - Apply sizing and styling
 * - Manage z-index layering
 */
@Component({
  selector: 'app-dynamic-modal-dialog',
  standalone: true,
  imports: [ButtonComponent, IconComponent, ClickOutsideDirective, TranslatePipe],
  templateUrl: './dynamic-modal-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./dynamic-modal-dialog.component.css', '../../../../css/modal-base.css'],
})
export class DynamicModalDialogComponent {
  @Input() config!: ModalConfig;
  @Output() closeModal = new EventEmitter<ModalResult | undefined>();
  @Output() cancelModal = new EventEmitter<ModalResult | undefined>();

  private service = inject(ModalService);
  private ngZone = inject(NgZone);

  /** Handle header close button click */
  protected onCloseClick(): void {
    this.closeModal.emit({ action: 'close', reason: 'header_close_button' });
  }

  /** Handle clicking outside the modal dialog */
  protected onBackdropClick(): void {
    if (this.config?.closeOnClickOutside !== false) {
      this.closeModal.emit({ action: 'cancel', reason: 'backdrop_click' });
    }
  }

  /** Handle Escape key press */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.config?.closeOnEscape !== false) {
      this.cancelModal.emit({ action: 'cancel', reason: 'escape_key' });
    }
  }

  /** Handle action button click */
  protected onActionButtonClick(button: ModalActionButton, event: MouseEvent): void {
    button.onClick({
      originalEvent: event,
      button,
    });
  }

  /** Check if a button is disabled (supports both boolean and Signal<boolean> and Signal<string | null>) */
  protected isButtonDisabled(button: ModalActionButton): boolean {
    if (typeof button.disabled === 'boolean') {
      return button.disabled;
    }
    // For signals, call them and interpret the result
    const result = button.disabled?.();
    // Signal<boolean> returns boolean; Signal<string | null> returns string | null
    // Either way: truthy value = disabled, falsy = enabled
    return !!result;
  }

  /** Check if a button is in loading state */
  protected isButtonLoading(button: ModalActionButton): boolean {
    return button.loading?.() ?? false;
  }

  /** Map modal button variant to ButtonComponent variant */
  protected getButtonVariant(button: ModalActionButton): 'filled' | 'outlined' | 'text' | 'icon' {
    switch (button.variant) {
      case 'primary':
      case 'danger':
        return 'filled';
      case 'secondary':
        return 'outlined';
      case 'text':
        return 'text';
      default:
        return 'text';
    }
  }

  /** Get the z-index for this modal layer */
  protected getZIndex(): number {
    return this.service.getZIndex();
  }

  /** Get modal size CSS attribute value */
  protected getSize(): string {
    return this.config?.size || 'medium';
  }

  /** Whether the header should be shown */
  protected get showHeader(): boolean {
    return !!(this.config?.title || this.config?.showCloseButton !== false);
  }

  /** Whether the footer should be shown */
  protected get showFooter(): boolean {
    return !!(this.config?.actionButtons && this.config.actionButtons.length > 0);
  }
}
